import { useMemo, useState } from 'react'
import {
  Plus,
  Palette,
  LayoutList,
  LayoutGrid,
  Image,
  Circle,
  PictureInPicture,
  Layers,
  PlaySquare,
  GalleryHorizontal,
  GalleryThumbnails,
  Tv2,
} from 'lucide-react'
import { labelForDisplayType } from '../../constants/displayTypes'
import styles from './SurfaceRail.module.css'

const DISPLAY_TYPE_ICONS = {
  story_block: LayoutList,
  carousel: GalleryHorizontal,
  grid: LayoutGrid,
  hero: Image,
  circle_stories: Circle,
  floating_player: PictureInPicture,
  player_deck: Layers,
  horizontal_player: PlaySquare,
  horizontal_carousel: GalleryHorizontal,
  dynamic_carousel: GalleryThumbnails,
  vertical_stories: Tv2,
}

function countOverrides(overrides) {
  if (!overrides || typeof overrides !== 'object') return 0
  let count = 0
  for (const value of Object.values(overrides)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      count += countOverrides(value)
    } else if (value !== undefined && value !== null && value !== '') {
      count += 1
    }
  }
  return count
}

/**
 * Left-rail surface chooser for the Configuration workspace.
 *
 * Entries:
 *   - Branded content (always first, always present)
 *   - Divider
 *   - Enabled surfaces, then disabled surfaces (behind Show disabled)
 *   - + Add surface CTA
 *
 * Selection is owned by the parent; the rail only emits onSelect with
 * either 'baseline' or a surface.id.
 */
export default function SurfaceRail({
  surfaces = [],
  selectedId, // 'baseline' | surface.id
  onSelect,
  onAddSurface,
  dirty = false,
}) {
  const [showDisabled, setShowDisabled] = useState(false)

  const { enabled, disabled } = useMemo(() => {
    const e = []
    const d = []
    for (const surface of surfaces) {
      if (surface.is_enabled) e.push(surface)
      else d.push(surface)
    }
    // Sort: include_by_default first, then alphabetical
    const byName = (a, b) => a.name.localeCompare(b.name)
    e.sort((a, b) => {
      if (a.include_by_default !== b.include_by_default) {
        return a.include_by_default ? -1 : 1
      }
      return byName(a, b)
    })
    d.sort(byName)
    return { enabled: e, disabled: d }
  }, [surfaces])

  return (
    <nav className={styles.rail} aria-label="Configuration entries">
      <RailItem
        id="baseline"
        label="Branded content"
        subtitle="Shared branding"
        icon={Palette}
        isSelected={selectedId === 'baseline'}
        onSelect={() => onSelect('baseline')}
        dirtyIndicator={dirty && selectedId === 'baseline'}
      />

      <div className={styles.sectionLabel}>Surfaces</div>

      {enabled.length === 0 ? (
        <div className={styles.emptyHint}>No surfaces yet.</div>
      ) : (
        enabled.map((surface) => (
          <SurfaceRow
            key={surface.id}
            surface={surface}
            isSelected={selectedId === surface.id}
            onSelect={() => onSelect(surface.id)}
            dirtyIndicator={dirty && selectedId === surface.id}
          />
        ))
      )}

      {disabled.length > 0 && (
        <>
          <button
            type="button"
            className={styles.showDisabledToggle}
            onClick={() => setShowDisabled((s) => !s)}
            data-testid="surface-rail-show-disabled"
          >
            {showDisabled ? 'Hide' : 'Show'} disabled ({disabled.length})
          </button>
          {showDisabled &&
            disabled.map((surface) => (
              <SurfaceRow
                key={surface.id}
                surface={surface}
                isSelected={selectedId === surface.id}
                onSelect={() => onSelect(surface.id)}
                dirtyIndicator={dirty && selectedId === surface.id}
                isDisabled
              />
            ))}
        </>
      )}

      <button
        type="button"
        className={styles.addButton}
        onClick={onAddSurface}
        data-testid="surface-rail-add"
      >
        <Plus size={14} />
        Add surface
      </button>
    </nav>
  )
}

function RailItem({
  id,
  label,
  subtitle,
  icon: Icon,
  isSelected,
  onSelect,
  badgeLabel,
  badgeCount,
  trailingLabel,
  dirtyIndicator = false,
  muted = false,
}) {
  return (
    <button
      type="button"
      className={[
        styles.item,
        isSelected ? styles.itemSelected : '',
        muted ? styles.itemMuted : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onSelect}
      data-testid={`surface-rail-item-${id}`}
      aria-pressed={isSelected}
    >
      <span className={styles.itemIcon}>
        <Icon size={16} />
      </span>
      <span className={styles.itemBody}>
        <span className={styles.itemLabel}>
          {label}
          {dirtyIndicator ? <span className={styles.dirtyDot} aria-label="Unsaved changes" /> : null}
        </span>
        {subtitle ? <span className={styles.itemSubtitle}>{subtitle}</span> : null}
      </span>
      {badgeCount ? (
        <span className={styles.badge} title={badgeLabel || `${badgeCount} overrides`}>
          {badgeCount}
        </span>
      ) : null}
      {trailingLabel ? <span className={styles.trailing}>{trailingLabel}</span> : null}
    </button>
  )
}

function SurfaceRow({ surface, isSelected, onSelect, dirtyIndicator, isDisabled = false }) {
  const Icon = DISPLAY_TYPE_ICONS[surface.display_type] || LayoutList
  const overrideCount = countOverrides(surface.theme_overrides)
  const trailing = isDisabled ? 'off' : surface.include_by_default ? 'default' : null
  return (
    <RailItem
      id={surface.id}
      label={surface.name}
      subtitle={labelForDisplayType(surface.display_type)}
      icon={Icon}
      isSelected={isSelected}
      onSelect={onSelect}
      badgeLabel={`${overrideCount} override${overrideCount === 1 ? '' : 's'}`}
      badgeCount={overrideCount}
      trailingLabel={trailing}
      dirtyIndicator={dirtyIndicator}
      muted={isDisabled}
    />
  )
}
